/**
 * The `resolution` area: what the resolver says (src/domain/resolution/,
 * docs/RESOLUTION.md). A statement is the plan's one sentence, the one the log
 * stores as the origin marker's `statement`; a consequence is what a plan does
 * beyond its word; a question, its answers and its refusals are the rest.
 *
 * Statements name seats, never people: the domain holds refs, not names.
 */
export const resolution = {
  // --- Statements ------------------------------------------------------------
  'resolution.statement.delivered': 'Marked ready for review.',
  'resolution.statement.closed': 'Finished.',
  /** The edges of the finishing lifecycle (docs/CLOSED_AXIS.md "Finishing"). */
  'resolution.statement.done': 'Done.',
  'resolution.statement.readyForReview': 'Ready for review.',
  'resolution.statement.acceptedAndDone': 'Accepted and done.',
  /** The Review required setting turned on and off. */
  'resolution.statement.reviewRequired': 'Review required.',
  'resolution.statement.reviewNotRequired': 'Review not required.',
  // The statements plans made before 2026-11-03 still stand in the log as
  // keys (the origin marker's descriptor, docs/I18N.md): kept so the feed can
  // say them, in today's words.
  'resolution.statement.deliveredAndClosed': 'Done.',
  'resolution.statement.seated': 'Seated as contributor.',
  'resolution.statement.seatedClosed': 'Seated as contributor. Done.',
  'resolution.statement.seatedDeliveredAndClosed': 'Seated as contributor. Done.',
  'resolution.statement.deliveredWaitsOnReview': 'Ready for review.',
  'resolution.statement.seatedWaitsOnReview': 'Seated as contributor. Waits on the review.',
  'resolution.statement.seatedDeliveredWaitsOnReview': 'Seated as contributor. Ready for review.',
  'resolution.statement.accepted': 'Accepted.',
  'resolution.statement.denied': 'Denied.',
  'resolution.statement.verdictWithdrawn': 'Verdict withdrawn.',
  'resolution.statement.deliveryTakenBack': 'Marked not done.',
  'resolution.statement.reopened': 'Reopened.',
  'resolution.statement.cancelled': 'Cancelled.',
  'resolution.statement.takenBack': 'Taken back.',
  'resolution.statement.markedActive': 'Marked active.',
  'resolution.statement.markedIdle': 'Marked idle.',
  'resolution.statement.commented': 'Commented.',
  'resolution.statement.acknowledged': 'Acknowledged.',
  'resolution.statement.ownerSet': 'Requester set.',
  'resolution.statement.doerSet': 'Contributor set.',
  'resolution.statement.changed': 'Changed.',
  /** `tags`: one or more `#path` tokens, space-separated. */
  'resolution.statement.tagged': 'Tagged {tags}.',
  'resolution.statement.untagged': 'Removed #{tag}.',
  'resolution.statement.renamed': 'Renamed.',
  'resolution.statement.renamedAndTagged': 'Renamed; tagged {tags}.',
  'resolution.statement.retagged': 'Changed #{from} to #{to}.',
  'resolution.statement.tagRenamed': {
    one: 'Renamed #{from} to #{to} on {count} item.',
    other: 'Renamed #{from} to #{to} on {count} items.',
  },
  'resolution.statement.moved': 'Moved to "{stage}".',

  // --- Consequences ----------------------------------------------------------
  'resolution.consequence.dependenciesOpen': {
    one: '{count} item it depends on is still open.',
    other: '{count} items it depends on are still open.',
  },
  'resolution.consequence.subTasksStayOpen': {
    one: '{count} sub-item stays open.',
    other: '{count} sub-items stay open.',
  },
  /** What a conclusion takes with it below: finished under Done or Accept,
   *  cancelled under Cancel (a swept child takes its parent's reading). */
  'resolution.consequence.alsoFinishesSubTasks': {
    one: 'Also finishes {count} sub-item.',
    other: 'Also finishes {count} sub-items.',
  },
  'resolution.consequence.alsoCancelsSubTasks': {
    one: 'Also cancels {count} sub-item.',
    other: 'Also cancels {count} sub-items.',
  },

  // --- The conclusion question (§6) -------------------------------------------
  'resolution.question.subTasksOpen.done': {
    one: '{count} sub-item is still open. Finish it too?',
    other: '{count} sub-items are still open. Finish them too?',
  },
  'resolution.question.subTasksOpen.cancel': {
    one: '{count} sub-item is still open. Cancel it too?',
    other: '{count} sub-items are still open. Cancel them too?',
  },
  /** `count`: the open sub-items the answer speaks about. */
  'resolution.answer.finishToo': { one: 'Finish it too', other: 'Finish them too' },
  'resolution.answer.cancelToo': { one: 'Cancel it too', other: 'Cancel them too' },
  'resolution.answer.finishAll': 'Finish them all',
  'resolution.answer.cancelAll': 'Cancel them all',
  'resolution.answer.leaveOpen': { one: 'Leave it open', other: 'Leave them open' },
  /** A group of sub-items: `subject` is up to two titles (a list) or a count
   *  (`resolution.subject.subTasks`); `count` is how many it names. */
  'resolution.stays.tied': {
    one: '{subject} stays open: other work still needs it.',
    other: '{subject} stay open: other work still needs them.',
  },
  'resolution.stays.locked': {
    one: '{subject} stays open: not yours to conclude.',
    other: '{subject} stay open: not yours to conclude.',
  },
  'resolution.stays.held': {
    one: '{subject} stays open: its parent stays open.',
    other: '{subject} stay open: their parents stay open.',
  },
  'resolution.subject.subTasks': { one: '{count} sub-item', other: '{count} sub-items' },
  'resolution.force.closesNamed': 'Takes {titles}, which other work needs.',
  'resolution.force.alsoClosesNamed': 'Also takes {titles}, which other work needs.',
  'resolution.force.closesMore': {
    one: 'Takes {count} more sub-item that other work needs.',
    other: 'Takes {count} more sub-items that other work needs.',
  },
  'resolution.force.alsoClosesMore': {
    one: 'Also takes {count} more sub-item that other work needs.',
    other: 'Also takes {count} more sub-items that other work needs.',
  },
  /** A locked child wears the SEAT, never a person (docs/DESIGN_SYSTEM.md). */
  'resolution.subject.requesterOnly': 'requester only',

  // --- Permissions (§1 step 5) ------------------------------------------------
  'resolution.lane.viewOnly': 'You can see this item, not act on it.',
  'resolution.lane.ownerReach': 'Replacing the requester needs owner authority; ask an owner.',
  'resolution.lane.ruleNeedsAnswer':
    'The "{questionClass}" question needs an answer this rule does not carry.',
  'resolution.question.outOfRole':
    'This is not yours to decide, and the log will say so. Go ahead?',
  'resolution.answer.goAhead': 'Go ahead',
  'resolution.answer.leaveIt': 'Leave it',

  // --- Refusals ----------------------------------------------------------------
  'resolution.refusal.nothingToDo': 'Nothing to do.',
  'resolution.refusal.alreadyClosed': 'Already done or cancelled.',
  /** The edge table's refusals (src/domain/lifecycle-edges.ts `edgeRefusal`). */
  'resolution.refusal.alreadyEnded': 'Already done or cancelled. Reopen it first.',
  'resolution.refusal.alreadyOpen': 'Already open.',
  'resolution.refusal.alreadyInReview': 'Already in review.',
  'resolution.refusal.inReview': 'It is in review. The reviewer accepts it.',
  'resolution.refusal.reviewRequired':
    'A review is required here. Mark it ready for review, or turn the requirement off first.',
  'resolution.refusal.nobodyToReview': 'Nobody else is on this item to review it.',
  'resolution.refusal.stageHandsIn': 'The flow hands the work in. Move it to the next stage.',
  'resolution.refusal.notInReview': 'It is not in review.',
  'resolution.refusal.reviewersAct': 'Accept and Deny are the reviewer’s acts.',
  'resolution.refusal.waitsOnReview': 'It waits on the review.',
  'resolution.refusal.verdictIsRecord':
    'The verdict is the record of a review. Accept or deny the work instead.',
  'resolution.refusal.alreadyThere': 'Already there; nothing to do.',
  'resolution.refusal.leftAlone': 'Left alone; nothing was done.',
  'resolution.refusal.nothingToTakeBack': 'There is no statement to take back here.',
  'resolution.refusal.notYoursToTakeBack':
    'Part of that statement is not yours to take back; nothing was done.',
  'resolution.refusal.partCannotBeTakenBack':
    'Part of that statement cannot be taken back here; nothing was done.',
  'resolution.refusal.noInverse':
    '"{command}" cannot be taken back on its own; act on the item instead.',
  'resolution.refusal.titleNeeded': 'An item needs a title besides its tags.',
  'resolution.refusal.tagUnchanged': 'The tag is unchanged.',
  'resolution.refusal.notTagged': 'Not tagged #{tag}.',
  'resolution.refusal.notAMember': 'This item is not a member of that flow.',
  'resolution.refusal.noSuchStage': 'No stage "{stage}" in this flow.',
  'resolution.refusal.gateClosed': 'This item may not cross into that stage yet.',
  'resolution.refusal.tooLarge':
    'Too large for one gesture ({count} items). Finish the sub-items first.',
  'resolution.refusal.notGathered': 'The open subtree was not gathered; nothing was done.',
  'resolution.refusal.notYoursToConclude':
    'Some of that is not yours to conclude; nothing was done.',
  'resolution.refusal.notAnAnswer': '"{answer}" is not an answer to this question.',
  'resolution.refusal.nobodyToAnswer':
    'Nobody can answer the "{questionClass}" question here; nothing was done.',
  'resolution.refusal.ruleSkips':
    'This rule skips the "{questionClass}" question; nothing was done.',
  'resolution.refusal.noPolicy':
    'The "{questionClass}" question has no policy here; nothing was done.',
  'resolution.refusal.movedSinceLooked':
    'This item moved since you looked; nothing was done. Try again.',
  'resolution.refusal.movedDuringReplay':
    'This item moved while the queue was replayed; nothing was done.',
  /** A queued tap replayed offline cannot answer a question (docs/SYNC.md §7.4). */
  'resolution.refusal.queuedTapCannotAnswer':
    '{question} That needs an answer a queued tap cannot give.',
} as const;
